import { useMemo } from "react";
import { TrendingUp, TrendingDown, ArrowRightLeft, Wallet, DollarSign, BarChart3, Flame } from "lucide-react";
import { differenceInMonths, parseISO } from "date-fns";
import type { CashFlowRow, DisplayCurrency } from "@/hooks/finance/cash-flow/useCashFlow";
import { formatCurrency } from "@/lib/currency";

type Props = {
  rows: CashFlowRow[];
  periodKeys: string[];
  displayCurrency: DisplayCurrency;
  isLoading: boolean;
  dateFrom: string;
  dateTo: string;
};

function sumRow(row: CashFlowRow | undefined, periodKeys: string[]): number {
  if (!row) return 0;
  let total = 0;
  for (const k of periodKeys) {
    total += row.values.get(k) ?? 0;
  }
  return total;
}

function findRow(rows: CashFlowRow[], key: string): CashFlowRow | undefined {
  return rows.find((r) => r.key === key);
}

export function SummaryCards({ rows, periodKeys, displayCurrency, isLoading, dateFrom, dateTo }: Props) {
  const metrics = useMemo(() => {
    const netOperating = sumRow(findRow(rows, "net-operating"), periodKeys);
    const netInvesting = sumRow(findRow(rows, "net-investing"), periodKeys);
    const netFinancing = sumRow(findRow(rows, "net-financing"), periodKeys);
    const netCashFlow = sumRow(findRow(rows, "net-cash-flow"), periodKeys);
    const openingRow = findRow(rows, "opening");
    const closingRow = findRow(rows, "closing");

    const openingBalance = openingRow?.values.get(periodKeys[0]) ?? 0;
    const closingBalance = closingRow?.values.get(periodKeys[periodKeys.length - 1]) ?? 0;

    const monthsInRange = Math.max(1, differenceInMonths(parseISO(dateTo), parseISO(dateFrom)));
    const monthlyBurnRate = netOperating / monthsInRange;

    return { netOperating, netInvesting, netFinancing, netCashFlow, openingBalance, closingBalance, monthlyBurnRate };
  }, [rows, periodKeys, dateFrom, dateTo]);

  const fmt = (v: number) => formatCurrency(v, displayCurrency);

  const cards = [
    {
      label: "Opening Balance",
      value: metrics.openingBalance,
      icon: Wallet,
      color: "text-slate-600",
      bg: "bg-slate-50",
      border: "border-slate-200",
    },
    {
      label: "Net Operating",
      value: metrics.netOperating,
      icon: BarChart3,
      color: metrics.netOperating >= 0 ? "text-emerald-600" : "text-red-600",
      bg: metrics.netOperating >= 0 ? "bg-emerald-50" : "bg-red-50",
      border: metrics.netOperating >= 0 ? "border-emerald-200" : "border-red-200",
    },
    {
      label: "Net Investing",
      value: metrics.netInvesting,
      icon: TrendingDown,
      color: metrics.netInvesting >= 0 ? "text-emerald-600" : "text-amber-600",
      bg: metrics.netInvesting >= 0 ? "bg-emerald-50" : "bg-amber-50",
      border: metrics.netInvesting >= 0 ? "border-emerald-200" : "border-amber-200",
    },
    {
      label: "Net Financing",
      value: metrics.netFinancing,
      icon: ArrowRightLeft,
      color: metrics.netFinancing >= 0 ? "text-emerald-600" : "text-violet-600",
      bg: metrics.netFinancing >= 0 ? "bg-emerald-50" : "bg-violet-50",
      border: metrics.netFinancing >= 0 ? "border-emerald-200" : "border-violet-200",
    },
    {
      label: "Period Net Flow",
      value: metrics.netCashFlow,
      icon: metrics.netCashFlow >= 0 ? TrendingUp : TrendingDown,
      color: metrics.netCashFlow >= 0 ? "text-emerald-700" : "text-red-700",
      bg: metrics.netCashFlow >= 0 ? "bg-emerald-50" : "bg-red-50",
      border: metrics.netCashFlow >= 0 ? "border-emerald-300" : "border-red-300",
    },
    {
      label: "Closing Balance",
      value: metrics.closingBalance,
      icon: DollarSign,
      color: "text-slate-700",
      bg: "bg-slate-50",
      border: "border-slate-300",
    },
    {
      label: "Monthly Burn Rate",
      value: metrics.monthlyBurnRate,
      icon: Flame,
      color: metrics.monthlyBurnRate >= 0 ? "text-emerald-600" : "text-amber-600",
      bg: metrics.monthlyBurnRate >= 0 ? "bg-emerald-50" : "bg-amber-50",
      border: metrics.monthlyBurnRate >= 0 ? "border-emerald-200" : "border-amber-200",
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
      {cards.map((card) => {
        const Icon = card.icon;
        const isNeg = card.value < 0;
        return (
          <div
            key={card.label}
            className={`rounded-xl border ${card.border} ${card.bg} p-3 flex flex-col gap-1.5 transition-all hover:shadow-md`}
          >
            <div className="flex items-center gap-1.5">
              <Icon className={`h-3.5 w-3.5 ${card.color}`} />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {card.label}
              </span>
            </div>
            <span className={`text-sm font-bold font-mono tabular-nums ${card.color}`}>
              {isNeg ? `(${fmt(Math.abs(card.value))})` : fmt(card.value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
